import React from 'react'
import { ClientSentiment, CompetitorsPosition, SentimentScale } from '@marketing-milk/interfaces'
import { radius, SurfacePalette, type } from '../../../tokens'
import { sectionNumberFor } from '../../sections'
import { PresentPage } from '../PresentPage'

interface Props {
  palette: SurfacePalette
  packetID: number
  sentiment: ClientSentiment | null | undefined
}

const SCALE_LABEL: Record<Exclude<SentimentScale, null>, string> = {
  yes: 'Yes',
  maybe: 'Maybe',
  no: 'No',
}

const COMPETITORS_LABEL: Record<Exclude<CompetitorsPosition, null>, string> = {
  below: 'Below',
  equal: 'Equal',
  above: 'Above',
}

const Answer = ({
  palette,
  label,
  value,
  notes,
}: {
  palette: SurfacePalette
  label: string
  value: string | null
  notes?: string | null
}) => (
  <div>
    <div
      style={{
        fontFamily: type.family.mono,
        fontSize: type.size.eyebrow,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: palette.textMuted,
        marginBottom: 8,
      }}
    >
      {label}
    </div>
    {value ? (
      <span
        style={{
          display: 'inline-block',
          padding: '6px 14px',
          borderRadius: radius.sm,
          border: `1px solid ${palette.accent}`,
          background: palette.surface,
          color: palette.text,
          fontFamily: type.family.sans,
          fontSize: type.size.bodyLg,
          fontWeight: type.weight.semibold,
        }}
      >
        {value}
      </span>
    ) : (
      <span
        style={{
          fontFamily: type.family.sans,
          fontSize: type.size.bodyLg,
          fontStyle: 'italic',
          color: palette.textFaint,
        }}
      >
        No answer recorded
      </span>
    )}
    {notes && notes.trim() && (
      <div
        style={{
          marginTop: 8,
          fontFamily: type.family.sans,
          fontSize: type.size.body,
          color: palette.textMuted,
          whiteSpace: 'pre-wrap',
        }}
      >
        {notes}
      </div>
    )}
  </div>
)

export const PresentClientSentiment = ({ palette, packetID, sentiment }: Props) => (
  <PresentPage
    palette={palette}
    number={sectionNumberFor('client-sentiment')}
    title="Client Sentiment"
    commentAnchor={{
      type: 'section',
      id: `${packetID}:client-sentiment`,
      label: 'Client Sentiment',
    }}
  >
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28, maxWidth: 720 }}>
      <Answer
        palette={palette}
        label="What did you like today?"
        value={sentiment?.likedToday ?? null}
      />
      <Answer
        palette={palette}
        label="Below / equal / above competitors?"
        value={
          sentiment?.competitorsPosition ? COMPETITORS_LABEL[sentiment.competitorsPosition] : null
        }
        notes={sentiment?.competitorsNotes}
      />
      <Answer
        palette={palette}
        label="Would you renew if your contract were up today?"
        value={sentiment?.wouldRenew ? SCALE_LABEL[sentiment.wouldRenew] : null}
        notes={sentiment?.wouldRenewNotes}
      />
      <Answer
        palette={palette}
        label="Would you recommend us?"
        value={sentiment?.wouldRecommend ? SCALE_LABEL[sentiment.wouldRecommend] : null}
        notes={sentiment?.wouldRecommendNotes}
      />
    </div>
  </PresentPage>
)
